import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import "./Obras.css";
import client from "../../sanity/SanityClient";
import { analytics } from "../../firebase/firebase";
import { logEvent } from "firebase/analytics";

const DIDEROT_CATALOGO =
  "https://tienda.diderot.art/search/?q=juani%2Bpavlovsky&mpage=3";

const query = `
*[
  _type == "obraExterna" &&
  !(_id in path("drafts.**"))
] | order(_createdAt desc) {
  _id,
  titulo,
  externalSource,
  "externalLink": url,
  previewImage{ asset->{ url, metadata{dimensions{width,height,aspectRatio}} } }
}
`;

/* ---------- utils ---------- */
function pickImage(doc){
  const a = doc.previewImage?.asset;
  if(!a) return {url:"", ratio:1};
  const d = a.metadata?.dimensions || {};
  const ratio = (typeof d.aspectRatio==="number" && d.aspectRatio>0)
    ? d.aspectRatio
    : (d.width && d.height ? d.width/d.height : 1);
  return { url: a.url || "", ratio: Number(ratio)||1 };
}

function shallowEqual(a,b){
  const ka=Object.keys(a), kb=Object.keys(b);
  if(ka.length!==kb.length) return false;
  for(const k of ka){ if(a[k]!==b[k]) return false; }
  return true;
}

/** Caja visible de la imagen “contain” dentro del figure de alto H */
function computeContainBox(figWidth, targetH, ratio){
  const r = Number(ratio) > 0 ? Number(ratio) : 1;
  const naturalHByWidth = figWidth / r;

  if(naturalHByWidth >= targetH){
    const w = targetH * r;
    const left = (figWidth - w)/2;
    return { w: Math.round(w), h: Math.round(targetH), left: Math.round(left), top: 0 };
  }else{
    const h = naturalHByWidth;
    const top = (targetH - h)/2;
    return { w: Math.round(figWidth), h: Math.round(h), left: 0, top: Math.round(top) };
  }
}

/* ---------- helpers responsive ---------- */
const isMobile = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width:600px)").matches;

/** Resuelve la var CSS --obras-row-h a píxeles reales (respeta clamp()) y se recalcula cuando cambia el wrapper */
function useResolvedRowPx(rootElRef){
  const [px, setPx] = useState(null);
  useEffect(()=>{
    const el = rootElRef.current;
    if(!el) return;

    const measure = () => {
      try{
        const probe = document.createElement("div");
        probe.style.position = "absolute";
        probe.style.visibility = "hidden";
        probe.style.pointerEvents = "none";
        probe.style.height = "var(--obras-row-h)";
        probe.style.width = "1px";
        el.appendChild(probe);
        const h = probe.getBoundingClientRect().height;
        el.removeChild(probe);
        setPx(Number.isFinite(h) && h>0 ? Math.round(h) : null);
      }catch{ setPx(null); }
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return ()=>ro.disconnect();
  }, [rootElRef]);

  return px;
}

function useCols(){
  const getCols = () => {
    const w = typeof window!=="undefined" ? window.innerWidth : 1200;
    return w<=600 ? 1 : w<=1000 ? 2 : 3;
  };
  const [cols,setCols]=useState(getCols);
  useEffect(()=>{
    const onR=()=>setCols(getCols());
    window.addEventListener("resize",onR);
    return ()=>window.removeEventListener("resize",onR);
  },[]);
  return cols;
}

function rowsByCols(items, cols){
  const out=[]; for(let i=0;i<items.length;i+=cols) out.push(items.slice(i,i+cols));
  return out;
}

function buildCatalogButtons(items){
  const map=new Map();
  for(const it of items){
    let label=(it.externalSource||"").trim();
    if(!label){
      try{ label=new URL(it.externalLink).hostname.replace(/^www\./,""); }
      catch{ label="Catálogo"; }
    }
    if(map.has(label)) continue;
    let href=it.externalLink;
    if(/diderot/i.test(label) || /diderot/i.test(href)) href=DIDEROT_CATALOGO;
    else{
      try{ const u=new URL(href); href=`${u.protocol}//${u.hostname}/`; } catch{}
    }
    map.set(label, href);
  }
  return Array.from(map, ([label,href])=>({label,href}));
}

/* ======================= COMPONENTE ======================= */
const Obras = () => {
  const [obras,setObras]=useState([]);
  const [loading,setLoading]=useState(true);
  const cols=useCols();

  const wrapperRef = useRef(null);
  const figRefs = useRef({});                 // id -> figure
  const [imgBox, setImgBox] = useState({});   // id -> {w,h,left,top}
  const [rowH, setRowH] = useState({});       // rowIndex -> px
  const prevRowHRef = useRef({});             // para conservar si no hay candidatos

  /* ---- base CSS resuelta (px reales) ---- */
  const baseRowPx = useResolvedRowPx(wrapperRef);

  /* ---- carga ---- */
  useEffect(()=>{
    let mounted=true;
    client.fetch(query)
      .then(data=>{
        if(!mounted) return;
        const list=(Array.isArray(data)?data:[])
          .map(d=>{
            const {url,ratio}=pickImage(d);
            return {
              _id:d._id,
              titulo:d.titulo||"",
              externalSource:(d.externalSource||"").trim(),
              externalLink:d.externalLink,
              url, ratio
            };
          }).filter(x=>x.url && x.externalLink);
        setObras(list);
        if(analytics){
          const fuentes=[...new Set(list.map(i=>i.externalSource||"desconocida"))];
          logEvent(analytics,"obras_fuentes_view",{fuentes:fuentes.join("|"), cantidad_obras:list.length});
        }
      })
      .catch(()=>mounted && setObras([]))
      .finally(()=>mounted && setLoading(false));
    return ()=>{ mounted=false; };
  },[]);

  const filas=useMemo(()=>rowsByCols(obras,cols),[obras,cols]);
  const catalogButtons=useMemo(()=>buildCatalogButtons(obras),[obras]);

  /* ---- medir y fijar ALTURA de cada fila + caja visible ---- */
  useLayoutEffect(()=>{
    if(!filas.length) return;

    let rafId = null;

    const compute = () => {
      const nextRowH = {};
      const nextBox = {};

      filas.forEach((row, rIdx) => {
        // Altura objetivo H
        let H;
        if (isMobile()) {
          // MOBILE: alto base estable (como en el segundo código)
          H = baseRowPx || 240;
        } else {
          // DESKTOP/TABLET: cálculo original por fila
          const candidates = row.map(it => {
            const el = figRefs.current[it._id];
            const w = el?.clientWidth || 0;
            const r = Number(it.ratio) > 0 ? Number(it.ratio) : 1;
            return (w>0) ? (w / r) : null;
          }).filter(h => h && Number.isFinite(h) && h>0);

          if(candidates.length){
            H = Math.min(...candidates);
            const minGuard = 120;
            const maxGuard = baseRowPx ? baseRowPx * 1.25 : Infinity;
            H = Math.max(minGuard, Math.min(H, maxGuard));
            H = Math.round(H);
          }else{
            const prev = prevRowHRef.current[rIdx];
            H = Number.isFinite(prev) && prev>0 ? prev : (baseRowPx || 240);
          }
        }

        nextRowH[rIdx] = H;

        // Caja visible por imagen
        row.forEach(it => {
          const el = figRefs.current[it._id];
          const W = el?.clientWidth || 0;

          if (W <= 0) {
            // no pises con 0; esperá a la próxima medición
            return;
          }

          let box = computeContainBox(W, H, it.ratio);

          if (isMobile()) {
            // MOBILE: top=0 y h=H (sin letterbox vertical)
            box = {
              w: Math.max(1, box.w || W),
              h: H,
              left: Math.max(0, box.left || 0),
              top: 0
            };
          }

          nextBox[it._id] = box;
        });
      });

      if(!shallowEqual(prevRowHRef.current, nextRowH)){
        prevRowHRef.current = nextRowH;
        setRowH(nextRowH);
      }
      setImgBox(prev => shallowEqual(prev, nextBox) ? prev : nextBox);
    };

    const ro = new ResizeObserver(() => {
      if(rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(compute);
    });

    Object.values(figRefs.current).forEach(el=>{ if(el) ro.observe(el); });
    // primer cálculo
    compute();

    const onWinResize = () => {
      if(rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(compute);
    };
    window.addEventListener("resize", onWinResize, { passive:true });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onWinResize);
      if(rafId) cancelAnimationFrame(rafId);
    };
  }, [filas, baseRowPx]);

  const handleArtworkClick=(obra)=>{
    if(analytics){
      logEvent(analytics,"outbound_obras_click",{
        obra_id:obra._id, titulo:obra.titulo||"", fuente:obra.externalSource||"", link:obra.externalLink
      });
    }
  };
  const handleCatalogClick=(label,href)=>{
    if(analytics){ logEvent(analytics,"catalogo_fuente_click",{fuente:label, link:href}); }
  };

  return (
    <div className="obras-wrapper" ref={wrapperRef}>
      {/* Hero */}
      <div className="obras-hero">
        <h1 className="obras-title">Obras en venta</h1>
        <p className="obras-desc">
          Estas obras están disponibles en tiendas virtuales externas. Explorá la
          selección y abrí la obra para verla en su marketplace.
        </p>
      </div>

      {/* Catálogo externo */}
      <div className="obras-catalog">
        <span className="obras-catalog-label">Ver catálogo completo en:</span>
        <div className="obras-catalog-buttons">
          {catalogButtons.map(btn=>(
            <a
              key={btn.label}
              href={btn.href}
              target="_blank"
              rel="noopener noreferrer external nofollow"
              className="obras-catalog-link"
              onClick={()=>handleCatalogClick(btn.label, btn.href)}
            >
              {btn.label}
            </a>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <p className="obras-info">Cargando obras…</p>
      ) : filas.length===0 ? (
        <p className="obras-info">No hay obras externas cargadas todavía.</p>
      ) : (
        <div className="obras-rows">
          {filas.map((row,rIdx)=>{
            const hasVertical = row.some(it => Number(it.ratio) > 0 && Number(it.ratio) < 0.95);
            const isLast = rIdx === filas.length - 1;

            return (
              <div
                className={`obras-row${hasVertical ? " has-vertical" : ""}${isLast ? " is-last" : ""}`}
                key={`row-${rIdx}`}
                style={{ "--row-box-h": rowH[rIdx] ? `${rowH[rIdx]}px` : undefined }}
              >
                {row.map(obra=>{
                  const b = imgBox[obra._id]; // importante: sin default 0
                  return (
                    <a
                      key={obra._id}
                      href={obra.externalLink}
                      target="_blank"
                      rel="noopener noreferrer external nofollow"
                      className="obra-card"
                      aria-label={`Ver en ${obra.externalSource || "tienda externa"}`}
                      style={{
                        "--ratio": obra.ratio,
                        ...(b && b.w > 0 && b.h > 0 ? {
                          "--box-w": `${b.w}px`,
                          "--box-h": `${b.h}px`,
                          "--box-left": `${b.left}px`,
                          "--box-top": `${b.top}px`,
                        } : {})
                      }}
                      onClick={()=>handleArtworkClick(obra)}
                    >
                      <figure
                        className="obra-figure"
                        data-id={obra._id}
                        data-ratio={obra.ratio}
                        ref={(el)=>{ if(el) figRefs.current[obra._id] = el; }}
                      >
                        <img
                          src={obra.url}
                          alt={obra.titulo || obra.externalSource || "Obra externa"}
                          loading="lazy"
                          draggable="false"
                          onContextMenu={(e)=>e.preventDefault()}
                          onDragStart={(e)=>e.preventDefault()}
                        />
                        <figcaption className="obra-overlay">
                          Ver en {obra.externalSource || "tienda"}
                        </figcaption>
                      </figure>

                      {obra.titulo && (
                        <span className="obra-title">{obra.titulo}</span>
                      )}
                    </a>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      <p className="obras-footnote">
        La compra se realiza en la plataforma externa indicada. Precios, envíos
        y stock pueden variar según el marketplace.
      </p>
    </div>
  );
};

export default Obras;
